import { SaveApiResMessage } from '@/actions';

/**
 * Handles a click on a copyable field: copies value to clipboard, shows a notification,
 * and optionally opens an external URL in a new tab.
 *
 * @param {string} text - Value to copy
 * @param {{
 *   label?: string,
 *   successTitle?: string,
 *   errorTitle?: string,
 *   openExternalInNewTab?: boolean,
 *   externalUrl?: string,
 *   externalOpenDelayMs?: number,
 * }} [options]
 * @returns {Promise<boolean>}
 */
export function handleCopyableFieldClick(text, options = {}) {
  const {
    label = 'Text',
    successTitle = 'Copied',
    errorTitle = 'Error',
    openExternalInNewTab = false,
    externalUrl,
    externalOpenDelayMs = 1000,
  } = options;

  const value = text ?? '-';

  const openExternalIfNeeded = () => {
    if (openExternalInNewTab && externalUrl) {
      setTimeout(() => {
        window.open(externalUrl, '_blank');
      }, externalOpenDelayMs);
    }
  };

  return navigator.clipboard
    .writeText(String(value))
    .then(() => {
      SaveApiResMessage({
        title: successTitle,
        message: `${label} "${value}" Copied`,
        mode: 'success',
      });
      openExternalIfNeeded();
      return true;
    })
    .catch(() => {
      SaveApiResMessage({
        title: errorTitle,
        message: `${label} "${value}" Not Copied`,
        mode: 'error',
      });
      openExternalIfNeeded();
      return false;
    });
}

/** Click on field value: copy + notification only. */
export function handleCopyableNameClick(e, text, options = {}) {
  e.preventDefault();
  return handleCopyableFieldClick(text, options);
}

/** Click on external-link icon: copy + notification + open URL in new tab. */
export function handleCopyableIconClick(e, text, externalUrl, options = {}) {
  e.preventDefault();
  e.stopPropagation();
  return handleCopyableFieldClick(text, {
    ...options,
    openExternalInNewTab: true,
    externalUrl,
  });
}
